import * as React from 'react'

import { SearchIcon, XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import FilterHandleButton from '~/client/src/shared/components/Filters/components/FilterHandleButton/FilterHandleButton'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import { areObjectsEqual } from '~/client/src/shared/utils/util'

import EntityNameFilterStore from './EntityNameFilter.store'

// localization: translated

interface IEntityNameFilter {
  filters: IFilters
  onShowChanged?: (isShown: boolean) => void
  placeholder?: string
  className?: string
  forceClose?: boolean
  openOnLeft?: boolean
  shouldShowBaseText?: boolean
  icon?: React.ReactNode
  onSearchChange?: (key: string) => void
  searchKey?: string
  testId?: string
}

const doneTypingInterval = 300

@observer
export default class EntityNameFilter extends React.Component<IEntityNameFilter> {
  @observable private localProps: IEntityNameFilter = null
  public static defaultProps = {
    className: '',
  }

  private readonly store: EntityNameFilterStore

  public constructor(props: IEntityNameFilter) {
    super(props)

    this.localProps = props

    this.store = new EntityNameFilterStore(props.filters, props.onShowChanged)
    makeObservable(this)
  }

  @action
  public componentDidUpdate() {
    if (!areObjectsEqual(this.localProps.filters, this.props.filters))
      this.localProps.filters = this.props.filters
    if (
      this.props.searchKey !== undefined &&
      this.store.searchKey !== this.props.searchKey
    ) {
      this.store.searchKey = this.props.searchKey
      this.store.update()
    }
    if (this.props.forceClose) {
      this.store.isShown = false
    }
  }

  public render() {
    const { placeholder, className, shouldShowBaseText, icon, testId } =
      this.props
    const { isShown, isActive } = this.store

    return (
      <div className="activity-name-filter" data-testid={testId}>
        <FilterHandleButton
          onClick={this.store.toggle}
          className="tw:min-w-[50px]"
          isActive={isActive}
          isSelected={isShown}
          hasIndent={false}
        >
          {icon || <SearchIcon size={20} />}
          {!isShown && shouldShowBaseText && (
            <span
              className={classList({
                'tw:ml-[5px]': !icon,
                'tw:ml-0 tw:mr-1.5': !!icon,
              })}
            >
              {Localization.translator.search}
            </span>
          )}
        </FilterHandleButton>
        <div
          className={classList({
            'tw:flex tw:items-center tw:justify-start tw:px-[5px] tw:h-[60px] tw:min-w-[200px] tw:absolute tw:z-[12] no-outline-container': true,
            'tw:right-0': this.props.openOnLeft,
            [className]: true,
            'tw:hidden': !isShown,
          })}
        >
          {isShown && (
            <StruxhubInput
              placeholder={placeholder || Localization.translator.search}
              autoFocus={true}
              isMinimalisticMode={true}
              value={this.store.searchKey}
              onChange={this.updateSearchKey}
              withDelay={true}
              inputDelay={doneTypingInterval}
              customRightIcon={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="tw:flex-none! tw:text-muted-foreground"
                  aria-label={Localization.translator.clear}
                  onMouseDown={this.clearAndClose}
                  onClick={this.clearAndClose}
                >
                  <XIcon />
                </Button>
              }
              testId={testId ? `${testId}-input` : undefined}
            />
          )}
        </div>
      </div>
    )
  }

  @action.bound
  private updateSearchKey(event: React.ChangeEvent<HTMLInputElement>) {
    this.store.searchKey = event.target.value
    this.store.update()
    this.props.onSearchChange?.(event.target.value)
  }

  @action.bound
  private clearAndClose(event: React.SyntheticEvent<HTMLButtonElement>) {
    event.stopPropagation()
    this.store.clearAndClose()
    this.props.onSearchChange?.('')
  }
}
